import { MessageCircle, Users, Share2, HelpCircle, Mail, Phone, MapPin, Send, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";

const Community = () => {

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeInUp: any = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const staggerContainer: any = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const scaleIn: any = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const benefits = [
    {
      icon: Users,
      title: "Connect with Fellow Aspirants",
      description: "Join a community of dedicated UPSC aspirants sharing the same journey and goals.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Share2,
      title: "Share Strategies",
      description: "Exchange study strategies, tips, and insights with experienced and fellow aspirants.",
      color: "from-purple-500 to-violet-500"
    },
    {
      icon: HelpCircle,
      title: "Get Your Doubts Cleared",
      description: "Ask questions and get answers from peers and mentors in our active community.",
      color: "from-amber-500 to-orange-500"
    },
    {
      icon: MessageCircle,
      title: "Stay Updated",
      description: "Receive updates, announcements, and important information about UPSC preparation.",
      color: "from-emerald-500 to-teal-500"
    },
  ];
  const handleTelegramJoin = () => {
    window.open("https://t.me/+rtHIxK3rAH02ZGE1", "_blank");
  };

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}

      <section style={{ backgroundImage: "url('/herobg.webp')" }} className="relative bg-black min-h-screen flex justify-center items-center p-2 md:p-4 m-2 md:m-4 rounded-2xl mt-0 overflow-hidden">

        <motion.div
          className="relative max-w-5xl mx-auto text-center space-y-8 px-4"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Badge */}
          <motion.div variants={fadeInUp} className="flex justify-center">
            <div className="inline-flex bg-white/20 backdrop-blur-md items-center gap-2 px-3 py-2.5 rounded-full text-sm font-semibold bg-white/5 border border-white/10 text-white shadow-xl hover:bg-white/10 transition-colors duration-300">
              <Sparkles className="w-4 h-4 text-purple-300" />
              <span className="tracking-wide">Join Our Community</span>
            </div>
          </motion.div>

          {/* Heading */}
          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1]">
            <span className="text-white drop-shadow-xl">Join the</span>{" "}
            <span className="bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 bg-clip-text text-transparent drop-shadow-2xl filter brightness-125">
              Parakh Community
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p variants={fadeInUp} className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light">
            Connect with fellow aspirants, share strategies, and get your doubts cleared on our exclusive
            Telegram channel. Be part of a supportive community dedicated to UPSC success.
          </motion.p>

          {/* Button */}
          <motion.div variants={fadeInUp} className="pt-4">
            <motion.button
              onClick={handleTelegramJoin}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="h-14 px-10 text-lg bg-white text-indigo-950 hover:bg-indigo-50 font-bold rounded-full shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_-15px_rgba(255,255,255,0.5)] transition-all duration-300 inline-flex items-center gap-2"
            >
              <Send className="w-5 h-5" />
              Join on Telegram
            </motion.button>
          </motion.div>
        </motion.div>
      </section>

      {/* Benefits Section */}
      <section className="container py-24">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-20 mx-auto">
            <span className="text-sm font-bold text-indigo-600 uppercase tracking-widest pl-1">
              BENEFITS
            </span>
            <h2 className="text-5xl lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1]">
              Why Join <span className="text-indigo-600">Our Community?</span>
            </h2>
            <p className="text-xl text-gray-500 leading-relaxed font-light">
              Be part of a thriving community of UPSC aspirants helping each other succeed.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="group h-full p-10 rounded-[32px] bg-gray-50 border border-transparent hover:border-gray-200 hover:bg-white hover:shadow-xl transition-all duration-500">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-8 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <benefit.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900 tracking-tight">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed font-medium">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Community Guidelines */}
      {/* <section className="container px-2 py-24">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
           
            <motion.div variants={fadeInUp} className="space-y-12 px-2 md:px-4">
              <div className="space-y-6">
                <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 tracking-tighter leading-[1.1]">
                  Community <span className="text-indigo-600">Guidelines</span>
                </h2>
                <p className="text-xl text-gray-500 font-light leading-relaxed">
                  To ensure a positive and productive environment for everyone, we ask all members to respect these core principles.
                </p>
              </div>

              <div className="space-y-10">
                {guidelines.map((guideline, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="flex gap-6 items-start group"
                  >
                    <div className="flex-shrink-0">
                      <div className="text-5xl font-bold text-gray-900 leading-none">
                        {(index + 1).toString().padStart(2, '0')}<span className="text-gray-300">%</span>
                      </div>
                      <div className="h-1.5 w-12 bg-indigo-600 mt-3 rounded-full group-hover:w-20 transition-all duration-300" />
                    </div>
                    <div>
                      <p className="text-lg text-gray-600 font-medium leading-relaxed pt-2">
                        {guideline}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <p className="text-gray-400 italic">
                By joining, you agree to uphold these standards.
              </p>
            </motion.div>

            <motion.div variants={scaleIn} className="relative  h-full min-h-[500px] rounded-[40px] overflow-hidden bg-gray-100">
              <img
                src="/herobg.webp"
                alt="Community Values"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

              <div className="absolute bottom-10 left-10 right-10 p-6 bg-white/90 backdrop-blur-md rounded-3xl shadow-lg border border-white/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">10K+</div>
                    <div className="text-sm text-gray-500">Active Members</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section> */}

      {/* CTA Section */}
      <section className="container px-2 py-24 pb-32">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.div variants={scaleIn}>
            <div className="rounded-[40px] bg-indigo-600 overflow-hidden shadow-2xl relative">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                {/* Text Content */}
                <div className="p-10 md:p-16 space-y-8 text-left z-10">
                  <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1]">
                    Ready to join <br />
                    the <span className="text-indigo-200">Conversation?</span>
                  </h2>
                  <p className="text-lg text-indigo-100/90 font-light max-w-lg leading-relaxed">
                    Connect with thousands of aspirants, share strategies, and get real-time motivation.
                    <br /><br />
                    Your community is waiting.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 pt-2">
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <motion.button
                        onClick={handleTelegramJoin}
                        className="h-14 px-8 text-base bg-white text-indigo-600 hover:bg-white/90 font-bold rounded-xl shadow-lg transition-all duration-300 w-full sm:w-auto inline-flex items-center justify-center gap-2"
                      >
                        <Send className="w-5 h-5" />
                        Join on Telegram
                      </motion.button>
                    </motion.div>

                    <motion.div whileHover={{ x: 5 }} className="flex items-center">
                      <motion.button
                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                        className="h-14 px-6 text-base text-white hover:text-white hover:bg-white/10 font-medium rounded-xl transition-all duration-300 w-full sm:w-auto group inline-flex items-center justify-center"
                      >
                        Contact Us <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
                      </motion.button>
                    </motion.div>
                  </div>
                </div>

                {/* UI Image Representation */}
                 <div className="relative h-full min-h-[400px] lg:min-h-full bg-indigo-700/50 lg:bg-transparent overflow-hidden">
                  {/* This is a placeholder for the UI screenshot. Using herobg.jpg or similar. */}
                  {/* We position it to look like it's sliding in or part of a dashboard */}
                  <div className="absolute top-10 lg:top-16 left-10 lg:left-0 w-full h-full rounded-tl-[32px] overflow-hidden shadow-2xl border-t-8 border-l-8 border-gray-900 bg-gray-900">
                    

                    <motion.img
                      initial={{ x: 100, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 0.8 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      src="/ctaimg.webp"
                      alt="Platform Interface"
                      className="w-full h-full object-cover object-left hover:opacity-100 transition-opacity duration-500"
                    />

                    {/* Overlay to simulate UI depth if image isn't perfect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-transparent pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="container py-24 scroll-mt-20">
        <motion.div
          className="max-w-6xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="text-sm font-bold text-indigo-600 uppercase tracking-widest pl-1">
              Get in Touch
            </span>
            <h2 className="text-5xl lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1]">
              We'd love to <span className="text-indigo-600">hear from you.</span>
            </h2>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: MapPin, title: "Visit Us", content: "Kuberya-AI Office, Vijay Nagar, SBI Chowk, Jabalpur, Madhya Pradesh, India", sub: "India" },
                { icon: Phone, title: "Call Us", content: "+91 9009022958", sub: "Mon-Sat 9am-6pm" },
                { icon: Mail, title: "Email Us", content: "kuberyaai@gmail.com", sub: "We reply within 24h" }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  className="p-8 rounded-[32px] bg-gray-50 border border-transparent hover:bg-white hover:border-gray-100 hover:shadow-xl transition-all duration-300 group text-center"
                  whileHover={{ y: -5 }}
                >
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-white border border-gray-100 flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="w-7 h-7 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 font-medium">{item.content}</p>
                  <p className="text-gray-400 text-sm mt-1">{item.sub}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default Community;
